/** "Still working" cue (APP-3469): on a turn with many tool calls the trail's blinking head scrolls out of view,
 *  so mirror it at the foot of the transcript until the reply settles. */

import { Box, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { visuallyHidden } from "@mui/utils"
import type { ChatMessage } from "@mui/x-chat-headless"
import { CALM_PULSE_SX, ChatStatusPill, ChatStatusSpinner, FLOATING_PILL_SX, WorkingPulse } from "./chat-pill"
import { isMessageInFlight } from "./message-chrome"
import { getWorkingLabel } from "./thinking-trail"

// The clamp lives here, not on WorkingPulse: blockified as a flex item, this is where `overflow` applies.
const LABEL_SX = { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...CALM_PULSE_SX } satisfies SxProps<Theme>

/**
 * Is the turn the controller is reporting the one on screen? Read off the transcript rather than latched from
 * the controller flag: `isTurnActive` is view-agnostic and, because endTurn no-ops for a superseded controller,
 * it can stay true across a thread switch and a fresh send, so any latch keyed on it going false can miss a turn.
 * A turn in this view always has an in-flight tail — the prompt while it is sending, then the reply being written.
 */
export function isTurnVisibleInTranscript(messages: ChatMessage[]): boolean {
  const tail = messages[messages.length - 1]
  return tail != null && isMessageInFlight(tail)
}

export interface TurnStatus {
  /** A composer or programmatic turn is registered with the controller (withActiveTurn). */
  isTurnActive: boolean
  /** That turn is the one this transcript is showing — see isTurnVisibleInTranscript. */
  isTurnInThisView: boolean
  /** Reattaching to a thread's in-flight stream. Spans the whole resumed turn, which never registers as a turn. */
  isReattaching: boolean
  /** The reattach handshake window, where ReattachIndicator already occupies the cue's rect. */
  isReconnecting: boolean
}

/** Exported for coverage: the resumed-turn and handshake paths have no story harness (both need streaming). */
export function shouldShowWorkingCue({ isTurnActive, isTurnInThisView, isReattaching, isReconnecting }: TurnStatus): boolean {
  return ((isTurnActive && isTurnInThisView) || isReattaching) && !isReconnecting
}

/** The pill's label while the server is reconnecting to a stalled model provider. Names no model, repeats no provider error text. */
export const PROVIDER_RETRYING_LABEL = "Still working"

export interface WorkingCueProps {
  /** A turn is in flight — composer or programmatic (the controller's unified signal). */
  isActive: boolean
  /** The assistant turn being written, or null before it exists (the label then reads "Thinking"). */
  message: ChatMessage | null
  /** The server is reconnecting to a stalled model provider — the label reads PROVIDER_RETRYING_LABEL instead. Not a failure. */
  isProviderRetrying: boolean
}

/** The pill itself is aria-hidden: the message list is a `role="log"` live region and already announces the
 *  streamed turn. During a provider stall no tokens flow, so that region falls silent — the visually-hidden
 *  live region below sits OUTSIDE the aria-hidden pill (a hidden ancestor mutes any live region nested in it)
 *  and mounts only for the retry state, so it announces once on entry and stays out of the way otherwise. */
export function WorkingCue({ isActive, message, isProviderRetrying }: WorkingCueProps) {
  if (!isActive) return null
  return (
    <>
      <ChatStatusPill sx={FLOATING_PILL_SX} aria-hidden data-testid="chatbox-working-cue">
        <ChatStatusSpinner />
        <WorkingPulse pulsing sx={LABEL_SX}>
          <Typography component="span" variant="body2" color="inherit">
            {isProviderRetrying ? PROVIDER_RETRYING_LABEL : message == null ? "Thinking" : getWorkingLabel(message)}
          </Typography>
        </WorkingPulse>
      </ChatStatusPill>
      {isProviderRetrying && (
        <Box component="span" sx={visuallyHidden} role="status" aria-live="polite">
          {PROVIDER_RETRYING_LABEL}
        </Box>
      )}
    </>
  )
}
